// src/pages/DataImport.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, Upload, Database, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts';
import { supabase } from '../lib/supabaseClient';
import CSVUploadPatients from '../components/CSVUploadPatients';
import CSVUploadClaims from '../components/CSVUploadClaims';
import { getImportHistory } from '../services/dataImportService';
import { getPMSConfig, getLastSyncTime, syncPatientsFromPMS, syncClaimsFromPMS } from '../services/pmsSyncService';
import * as awsApi from '../services/awsApiService';
import { logDataImport } from '../services/auditService';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

const DataImport = () => {
  const navigate = useNavigate();
  const { user, practice } = useAuth();
  const [practiceId, setPracticeId] = useState(null);
  const [activeTab, setActiveTab] = useState('patients');
  const [importHistory, setImportHistory] = useState([]);
  const [pmsConfig, setPmsConfig] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // Fetch practice_id - use AWS API data or fallback to Supabase query
  useEffect(() => {
    const fetchPracticeId = async () => {
      if (useAWS) {
        if (practice?.practice_id) {
          setPracticeId(practice.practice_id);
          return;
        }
      }

      // Supabase mode: query directly from database
      if (!useAWS && user?.id) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('practice_id')
            .eq('id', user.id)
            .maybeSingle();

          if (!error && data?.practice_id) {
            setPracticeId(data.practice_id);
            return;
          }
        } catch (err) {
          // Fall through to final fallback
        }
      }

      // Final fallback: try practice object id
      if (practice?.id) {
        setPracticeId(practice.id);
      }
    };

    fetchPracticeId();
  }, [user, practice]);

  useEffect(() => {
    if (practiceId) {
      loadImportHistory();
      loadPMSConfig();
      loadLastSyncTime();
    }
  }, [practiceId]);

  const loadImportHistory = async () => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Import history query timeout')), 5000)
      );

      const history = await Promise.race([
        getImportHistory(practiceId),
        timeoutPromise
      ]);

      setImportHistory(history);
    } catch (error) {
      setImportHistory([]);
    }
  };

  const loadPMSConfig = async () => {
    try {
      if (useAWS) {
        const result = await awsApi.getOpenDentalConfig();
        if (result.success) {
          setPmsConfig({ system: result.data.system, config: result.data.config || {}, isConfigured: result.data.isConfigured });
          setLastSyncTime(result.data.lastSync);
          return;
        }
      }
      const config = await getPMSConfig(practiceId);
      setPmsConfig(config);
    } catch (error) {
      setPmsConfig({ system: 'csv_import', config: {}, isConfigured: false });
    }
  };

  const loadLastSyncTime = async () => {
    if (useAWS) return; // Loaded as part of loadPMSConfig in AWS mode
    try {
      const syncTime = await getLastSyncTime(practiceId);
      setLastSyncTime(syncTime);
    } catch (error) {
      setLastSyncTime(null);
    }
  };

  const handleUploadComplete = (results) => {
    loadImportHistory();
    setSyncMessage({
      type: 'success',
      text: `Successfully imported ${results.successful} records!`
    });
    setTimeout(() => setSyncMessage(null), 5000);
  };

  const handlePMSSync = async () => {
    setSyncing(true);
    setSyncMessage(null);

    try {
      const result = await awsApi.syncOpenDentalData('both');

      if (result.success) {
        const patients = result.results?.patients;
        const claims = result.results?.claims;
        const patientCount = patients ? (patients.inserted + patients.updated) : 0;
        const claimCount = claims ? (claims.inserted + claims.updated) : 0;
        setSyncMessage({
          type: 'success',
          text: `Sync complete — ${patientCount} patients, ${claimCount} claims updated.`
        });
        loadImportHistory();
        loadPMSConfig();
      } else {
        setSyncMessage({
          type: 'error',
          text: result.error || 'Sync failed. Check your Open Dental connection in Settings.'
        });
      }
    } catch (error) {
      setSyncMessage({
        type: 'error',
        text: error.message
      });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px', color: '#1e293b' }}>
                Data Import
              </h1>
              <p style={{ color: '#64748b' }}>
                Import patient and claims data from your PMS system
              </p>
            </div>
            
            {pmsConfig?.isConfigured && pmsConfig?.system === 'open_dental' && (
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                  Last sync: {formatDate(lastSyncTime)}
                </p>
                <button
                  onClick={handlePMSSync}
                  disabled={syncing}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: syncing ? '#94a3b8' : '#14b8a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  <Database size={18} />
                  {syncing ? 'Syncing...' : 'Sync from Open Dental'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sync Message */}
        {syncMessage && (
          <div style={{
            backgroundColor: syncMessage.type === 'success' ? '#f0fdfa' : '#fef2f2',
            border: `1px solid ${syncMessage.type === 'success' ? '#5eead4' : '#fecaca'}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {syncMessage.type === 'success' ? (
              <CheckCircle size={20} style={{ color: '#14b8a6' }} />
            ) : (
              <AlertCircle size={20} style={{ color: '#dc2626' }} />
            )}
            <span style={{ color: syncMessage.type === 'success' ? '#0f766e' : '#991b1b' }}>
              {syncMessage.text}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #e2e8f0',
            padding: '0 20px'
          }}>
            <button
              onClick={() => setActiveTab('patients')}
              style={{
                padding: '16px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                borderBottom: activeTab === 'patients' ? '2px solid #14b8a6' : '2px solid transparent',
                color: activeTab === 'patients' ? '#14b8a6' : '#64748b',
                fontWeight: activeTab === 'patients' ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Users size={18} />
              Import Patients
            </button>
            <button
              onClick={() => setActiveTab('claims')}
              style={{
                padding: '16px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                borderBottom: activeTab === 'claims' ? '2px solid #14b8a6' : '2px solid transparent',
                color: activeTab === 'claims' ? '#14b8a6' : '#64748b',
                fontWeight: activeTab === 'claims' ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FileText size={18} />
              Import Claims
            </button>
            <button
              onClick={() => setActiveTab('history')}
              style={{
                padding: '16px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                borderBottom: activeTab === 'history' ? '2px solid #14b8a6' : '2px solid transparent',
                color: activeTab === 'history' ? '#14b8a6' : '#64748b',
                fontWeight: activeTab === 'history' ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Clock size={18} />
              Import History
            </button>
          </div>

          {/* Tab Content */}
          <div style={{ padding: '30px' }}>
            {activeTab === 'patients' && (
              <CSVUploadPatients 
                practiceId={practiceId} 
                onUploadComplete={handleUploadComplete}
              />
            )}

            {activeTab === 'claims' && (
              <CSVUploadClaims 
                practiceId={practiceId} 
                onUploadComplete={handleUploadComplete}
              />
            )}

            {activeTab === 'history' && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#1e293b' }}>
                  Recent Import History
                </h3>
                {importHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <Upload size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                    <p>No import history yet</p>
                    <p style={{ fontSize: '14px', marginTop: '8px' }}>
                      Upload your first CSV file to get started
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {importHistory.map((record, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ fontWeight: '500', marginBottom: '4px', color: '#1e293b' }}>
                              {record.action === 'csv_import_patients' ? 'Patient Import' : 'Claims Import'}
                            </p>
                            <p style={{ fontSize: '14px', color: '#64748b' }}>
                              {formatDate(record.created_at)}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ color: '#14b8a6', fontWeight: '500' }}>
                              ✓ {record.metadata?.successful || 0} successful
                            </p>
                            {record.metadata?.failed > 0 && (
                              <p style={{ color: '#dc2626', fontSize: '14px' }}>
                                ✗ {record.metadata.failed} failed
                              </p>
                            )}
                            {record.metadata?.duplicates > 0 && (
                              <p style={{ color: '#f59e0b', fontSize: '14px' }}>
                                ⚠ {record.metadata.duplicates} duplicates
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* PMS Configuration Info */}
        {!pmsConfig?.isConfigured && (
          <div style={{
            backgroundColor: '#f0fdfa',
            border: '1px solid #5eead4',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'start',
            gap: '12px'
          }}>
            <AlertCircle size={20} style={{ color: '#0891b2', marginTop: '2px' }} />
            <div>
              <p style={{ fontWeight: '500', color: '#0f766e', marginBottom: '4px' }}>
                Want automatic syncing?
              </p>
              <p style={{ fontSize: '14px', color: '#0f766e', marginBottom: '8px' }}>
                If you use Open Dental, you can configure automatic data synchronization in Settings.
              </p>
              <button
                onClick={() => navigate('/settings')}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#14b8a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Configure PMS Integration
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataImport;